import { useState } from 'react';
import type { ItineraryItem } from '../utils/timesheetItinerary';
import { type CallLog, isVisitSigned, isVisitSkipped } from '../services/api';

interface TimesheetItineraryProps {
  items: ItineraryItem[];
  callLogs: CallLog[];
  onEdit: (log: CallLog) => void;
  onDelete: (log: CallLog) => void;
}

function fmtMin(min: number): string {
  if (min <= 0) return '—';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/**
 * Day itinerary view of the timesheet: drive legs + store visits, each visit
 * expanding to its projects (combined minutes) and each project to its discrete
 * clockings. Renders the same entries as the flat list — the parent only shows
 * this when the grouped totals match the flat totals.
 */
export default function TimesheetItinerary({ items, callLogs, onEdit, onDelete }: TimesheetItineraryProps) {
  const [openVisits, setOpenVisits] = useState<Set<string>>(new Set());
  const [openProjects, setOpenProjects] = useState<Set<string>>(new Set());

  const workLogForClocking = (callLogId?: number): CallLog | null => {
    if (!callLogId) return null;
    return (
      callLogs.find((l) => l.calllogid === callLogId && !(l.description_of_activity || '').includes('Drive')) ?? null
    );
  };

  return (
    <div className="space-y-2">
      {items.map((item, idx) => {
        if (item.kind === 'drive') {
          // A drive is recorded on the CallLog of the visit it led to, and that
          // visit's editor already has the drive start/end fields (#3019). The
          // row just never said so, so a rep with a 134-minute drive to fix had
          // to guess that the way to reach it was to tap the store BELOW it.
          // item.id is a ROW KEY (calllogid * 10), not a CallLog id — looking
          // the owner up by it never matched, so this row has never once been
          // clickable since #3019 shipped. Proven on the dev rig, not guessed.
          const owner = workLogForClocking(item.callLogId);
          const body = (
            <>
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div className="text-xs text-slate-500">
                <span className="font-medium text-slate-600">{item.label}</span>
                {item.start && item.end && (
                  <span className="text-slate-400">
                    {' · '}
                    {item.start} – {item.end}
                  </span>
                )}
                {' · '}
                {fmtMin(item.durationMin)}
                {item.miles != null && <span className="text-blue-600"> · {item.miles} mi</span>}
              </div>
            </>
          );

          // A signature seals the visit TIMES the manager attested to — never
          // the drive, which they were not shown (#3525) — and the editor
          // collects the reason whenever a sealed field changes.
          if (!owner) {
            return (
              <div key={`d-${item.id}-${idx}`} className="flex items-center gap-3 pl-3">
                {body}
              </div>
            );
          }

          return (
            <button
              key={`d-${item.id}-${idx}`}
              type="button"
              data-testid="edit-drive"
              onClick={() => onEdit(owner)}
              className="flex w-full items-center gap-3 rounded-lg pl-3 pr-2 py-1 text-left hover:bg-blue-50"
            >
              {body}
              <span className="ml-auto text-xs font-medium text-blue-600">Fix</span>
            </button>
          );
        }

        const visitKey = `${item.store}-${item.checkIn}-${idx}`;
        const isOpen = openVisits.has(visitKey);
        return (
          <div key={visitKey} className="overflow-hidden rounded-xl border border-green-200 bg-green-50">
            <button
              type="button"
              onClick={() =>
                setOpenVisits((prev) => {
                  const next = new Set(prev);
                  next.has(visitKey) ? next.delete(visitKey) : next.add(visitKey);
                  return next;
                })
              }
              className="flex w-full items-center gap-3 p-3 text-left"
            >
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="flex-1">
                <div className="font-medium text-slate-800">{item.store}</div>
                <div className="text-xs text-slate-500">
                  {item.checkIn} – {item.checkOut}
                  {' · '}
                  {item.projects.length} {item.projects.length === 1 ? 'project' : 'projects'}
                </div>
              </div>
              <div className="rounded-full bg-white px-2 py-1 text-xs font-medium text-slate-600">
                {item.hasActive ? 'Active' : fmtMin(item.totalMin)}
              </div>
              <svg
                className={`h-4 w-4 flex-shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isOpen && (
              <div className="space-y-1 border-t border-green-200 bg-white p-2">
                {item.projects.map((proj, pIdx) => {
                  const projKey = `${visitKey}-${proj.projectId ?? 'x'}-${pIdx}`;
                  const projOpen = openProjects.has(projKey);
                  return (
                    <div key={projKey} className="rounded-lg border border-slate-100">
                      <button
                        type="button"
                        onClick={() =>
                          setOpenProjects((prev) => {
                            const next = new Set(prev);
                            next.has(projKey) ? next.delete(projKey) : next.add(projKey);
                            return next;
                          })
                        }
                        className="flex w-full items-center gap-2 px-3 py-2 text-left"
                      >
                        <div className="flex-1 truncate text-sm text-primary-700">
                          {proj.projectId ? `#${proj.projectId} ` : ''}
                          {proj.projectDescription || 'Project'}
                        </div>
                        <div className="text-xs font-medium text-slate-600">{fmtMin(proj.totalMin)}</div>
                        {proj.clockings.length > 1 && (
                          <span className="text-[10px] text-slate-400">
                            {proj.clockings.length} clockings {projOpen ? '▾' : '▸'}
                          </span>
                        )}
                      </button>

                      {(projOpen || proj.clockings.length === 1) && (
                        <div className="space-y-1 px-3 pb-2">
                          {proj.clockings.map((c) => {
                            const log = workLogForClocking(c.callLogId);
                            const canEdit = c.durationMin >= 0 && !!log;
                            const signed = log ? isVisitSigned(log) : false;
                            const skipped = log ? isVisitSkipped(log) : false;
                            return (
                              <div
                                key={c.id}
                                className="flex items-center gap-2 rounded-md bg-slate-50 px-2 py-1.5 text-xs"
                              >
                                <span className="text-slate-500">
                                  {c.start} – {c.end}
                                </span>
                                {signed && (
                                  <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
                                    Signed
                                  </span>
                                )}
                                {skipped && (
                                  <span
                                    className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-700"
                                    title={log?.no_signature_reason || undefined}
                                  >
                                    No Sig
                                  </span>
                                )}
                                {log?.edited_after_signature && (
                                  <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                                    Edited
                                  </span>
                                )}
                                <span className="ml-auto font-medium text-slate-600">{fmtMin(c.durationMin)}</span>
                                {canEdit && log && (
                                  <>
                                    <button
                                      onClick={() => onEdit(log)}
                                      title="Edit visit"
                                      className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                                    >
                                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                      </svg>
                                    </button>
                                    {!signed && (
                                      <button
                                        onClick={() => onDelete(log)}
                                        title="Delete visit"
                                        className="rounded p-1 text-slate-400 hover:bg-red-100 hover:text-red-600"
                                      >
                                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
